import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';

import CoachOverlay from './CoachOverlay';
import { bringIntoView, clickTarget, waitForTarget } from './dom';
import { routeMatches } from './route';
import { subscribeAdvance } from './advance';
import { getGuide } from '../guides';
import { GUIDE_STATUS, persistProgress, persistProgressNow } from '../progress';
import { COACH_TELEMETRY, reportCoachEvent } from '../telemetry';

// ---------------------------------------------------------------------------
// The guide engine.
//
// One state machine drives every guide:
//
//   goToStep(i)
//     step has a route we are not on?  -> navigating -> (route matches)
//     wait for the target to paint     -> waiting    -> active | stalled
//     active + act/await               -> subscribe advance conditions
//     advance                          -> persist -> goToStep(i + 1)
//
// It lives above the router outlet so a guide survives every navigation it
// causes, and it is deliberately the only thing that knows how a guide runs.
// Everything else (launcher, dashboard card, Dex offers) just calls start().
// ---------------------------------------------------------------------------

const CoachContext = createContext(null);

const EMPTY_STEPS = [];

const IDLE = {
  guideId: null,
  stepIndex: 0,
  status: 'waiting',
  paused: false,
};

export function useCoach() {
  const ctx = useContext(CoachContext);
  if (!ctx) {
    // Safe no-op so a component rendered outside the provider (a test, a
    // standalone page) never crashes on a missing coach.
    return {
      running: false,
      activeGuideId: null,
      start: () => false,
      resume: () => {},
      stop: () => {},
      isRunning: () => false,
      canRun: () => false,
    };
  }
  return ctx;
}

// Permission lives with the app, not with the engine, so it arrives as a prop.
// Defaulting to "yes" keeps the engine runnable on its own (a test harness, a
// standalone page); the real app always passes the real check.
const ALLOW_ALL = () => true;

export function CoachProvider({
  children, enabled = true, onGuideFinished, canRun = ALLOW_ALL,
}) {
  const navigate = useNavigate();
  const location = useLocation();

  const [run, setRun] = useState(IDLE);
  const runRef = useRef(run);
  runRef.current = run;

  const guide = run.guideId ? getGuide(run.guideId) : null;
  // Stable identity: `steps` feeds every callback below, and a fresh [] each
  // render would rebuild them all and make the advance effect resubscribe on
  // every paint.
  const steps = useMemo(() => guide?.steps || EMPTY_STEPS, [guide]);
  const step = steps[run.stepIndex] || null;
  const total = steps.length;

  // Resolve a `branch` step to the concrete step it points at. Branching is how
  // one guide serves a single-campus org and a twelve-campus one without
  // shipping two copies of the same copy.
  const resolveIndex = useCallback(
    (index) => {
      let cursor = index;
      let hops = 0;
      while (steps[cursor]?.kind === 'branch' && hops < 10) {
        const target = steps[cursor].choose?.({ location }) ?? cursor + 1;
        cursor = typeof target === 'string' ? steps.findIndex((s) => s.id === target) : target;
        if (cursor < 0) return index + 1;
        hops += 1;
      }
      return cursor;
    },
    [steps, location]
  );

  // ---- transitions -------------------------------------------------------

  const goToStep = useCallback(
    (rawIndex) => {
      const index = resolveIndex(rawIndex);
      const next = steps[index];
      if (!next) return;
      const onRoute = routeMatches(next.route, location);
      setRun((r) => ({ ...r, stepIndex: index, status: onRoute ? 'waiting' : 'navigating' }));
      if (!onRoute && next.route) navigate(next.route);
    },
    [steps, location, navigate, resolveIndex]
  );

  const finish = useCallback(
    (status) => {
      const { guideId, stepIndex } = runRef.current;
      if (!guideId) return;
      persistProgressNow(guideId, {
        status,
        current_step: stepIndex,
        ...(status === GUIDE_STATUS.completed ? { completed_steps: [stepIndex] } : {}),
      });
      reportCoachEvent(
        status === GUIDE_STATUS.completed ? COACH_TELEMETRY.guideCompleted : COACH_TELEMETRY.guideAbandoned,
        { guideId, stepId: steps[stepIndex]?.id }
      );
      setRun(IDLE);
      if (onGuideFinished) onGuideFinished(guideId, status);
    },
    [steps, onGuideFinished]
  );

  const advance = useCallback(
    (reason) => {
      const { guideId, stepIndex } = runRef.current;
      if (!guideId) return;
      const current = steps[stepIndex];
      reportCoachEvent(
        reason === 'skipped' ? COACH_TELEMETRY.stepSkipped : COACH_TELEMETRY.stepAdvanced,
        { guideId, stepId: current?.id, meta: { reason } }
      );

      const nextIndex = resolveIndex(stepIndex + 1);
      if (nextIndex >= steps.length) {
        finish(GUIDE_STATUS.completed);
        return;
      }
      persistProgress(guideId, {
        status: GUIDE_STATUS.inProgress,
        current_step: nextIndex,
        completed_steps: [stepIndex],
      });
      goToStep(nextIndex);
    },
    [steps, goToStep, finish, resolveIndex]
  );

  const back = useCallback(() => {
    const prev = runRef.current.stepIndex - 1;
    if (prev < 0) return;
    persistProgress(runRef.current.guideId, { current_step: prev });
    goToStep(prev);
  }, [goToStep]);

  // ---- public controls ---------------------------------------------------

  const start = useCallback(
    (guideId, { at = 0 } = {}) => {
      const target = getGuide(guideId);
      if (!target) {
        console.warn(`[coach] no guide with id "${guideId}"`);
        return false;
      }
      // The last gate before a guide moves the browser. Callers that build
      // their own list have already filtered, but a pasted link has not, and
      // walking somebody into a 403 with a spotlight on it is the one failure
      // this module is not allowed to have.
      if (!canRun(guideId)) {
        console.warn(`[coach] guide "${guideId}" is not available to this user`);
        return false;
      }
      const startAt = Math.max(0, Math.min(at, target.steps.length - 1));
      reportCoachEvent(COACH_TELEMETRY.guideStarted, { guideId, meta: { at: startAt } });
      persistProgress(guideId, { status: GUIDE_STATUS.inProgress, current_step: startAt });

      const first = target.steps[startAt];
      const onRoute = routeMatches(first.route, location);
      setRun({ guideId, stepIndex: startAt, status: onRoute ? 'waiting' : 'navigating', paused: false });
      if (!onRoute && first.route) navigate(first.route);
      return true;
    },
    [location, navigate, canRun]
  );

  const pause = useCallback(() => {
    const { guideId, stepIndex } = runRef.current;
    if (!guideId) return;
    persistProgress(guideId, { status: GUIDE_STATUS.paused, current_step: stepIndex });
    reportCoachEvent(COACH_TELEMETRY.guidePaused, { guideId, stepId: steps[stepIndex]?.id });
    setRun((r) => ({ ...r, paused: true }));
  }, [steps]);

  const resume = useCallback(() => {
    setRun((r) => ({ ...r, paused: false }));
    goToStep(runRef.current.stepIndex);
  }, [goToStep]);

  const stop = useCallback(() => finish(GUIDE_STATUS.skipped), [finish]);

  const retry = useCallback(() => {
    setRun((r) => ({ ...r, status: 'waiting' }));
  }, []);

  const doItForMe = useCallback(() => {
    const current = steps[runRef.current.stepIndex];
    if (!current?.doItForMe) return;
    if (current.doItForMe.type === 'click') clickTarget(current.doItForMe.target);
    // No manual advance: the action fires the step's own advance condition, so
    // "do it for me" and "did it myself" travel exactly the same code path.
  }, [steps]);

  // ---- effects: navigating -> waiting -> active|stalled -------------------

  // Arrived at the step's route.
  useEffect(() => {
    if (!step || run.paused || run.status !== 'navigating') return;
    if (routeMatches(step.route, location)) {
      setRun((r) => ({ ...r, status: 'waiting' }));
    }
  }, [location, step, run.status, run.paused]);

  // Waiting for the target to paint.
  useEffect(() => {
    if (!step || run.paused || run.status !== 'waiting') return undefined;
    const startedAt = Date.now();
    // A narrate step may declare its target optional: point at the thing if it
    // is on screen, otherwise say the same words from a centred card. This is
    // for controls that are real but conditional - the sidebar on a phone, the
    // getting-started card once it has been dismissed - where stalling would be
    // a worse lie than simply not pointing. Only narrate steps qualify: an act
    // step with no hole leaves the blocker over the whole screen, so the user
    // could not do the thing being asked of them.
    const optional = step.optionalTarget && (step.kind || 'narrate') === 'narrate';
    const cancel = waitForTarget(step.target, (found) => {
      if (found) {
        bringIntoView(step.target);
        setRun((r) => ({ ...r, status: 'active' }));
        reportCoachEvent(COACH_TELEMETRY.stepShown, { guideId: run.guideId, stepId: step.id });
      } else if (optional) {
        setRun((r) => ({ ...r, status: 'active' }));
        reportCoachEvent(COACH_TELEMETRY.stepShown, {
          guideId: run.guideId,
          stepId: step.id,
          meta: { target_missing: true },
        });
      } else {
        setRun((r) => ({ ...r, status: 'stalled' }));
        reportCoachEvent(COACH_TELEMETRY.stepStalled, {
          guideId: run.guideId,
          stepId: step.id,
          meta: { waited_ms: Date.now() - startedAt, target: step.target, route: step.route },
        });
      }
    });
    return cancel;
  }, [step, run.status, run.paused, run.guideId]);

  // Advance conditions this module owns (click, dom, event).
  useEffect(() => {
    if (!step || run.paused || run.status !== 'active') return undefined;
    const kind = step.kind || 'narrate';
    if (kind !== 'act' && kind !== 'await') return undefined;
    return subscribeAdvance(step.advanceOn, (cond) => advance(cond?.type || 'auto'));
  }, [step, run.status, run.paused, advance]);

  // Route-type advance conditions, which need the router location.
  useEffect(() => {
    if (!step || run.paused) return;
    if (run.status !== 'active' && run.status !== 'stalled') return;
    const hit = (step.advanceOn || []).some((c) => c?.type === 'route' && routeMatches(c.path, location));
    if (hit) advance('route');
  }, [location, step, run.status, run.paused, advance]);

  // ---- context value -----------------------------------------------------

  const value = useMemo(
    () => ({
      running: !!run.guideId,
      activeGuideId: run.guideId,
      activeStepId: step?.id || null,
      paused: run.paused,
      start,
      resume,
      pause,
      stop,
      isRunning: (id) => run.guideId === id,
      // Exposed so a caller can decide not to render an offer at all, rather
      // than rendering a button that refuses when pressed.
      canRun,
    }),
    [run.guideId, run.paused, step, start, resume, pause, stop, canRun]
  );

  return (
    <CoachContext.Provider value={value}>
      {enabled && guide && step && (
        <CoachOverlay
          guideTitle={guide.title}
          step={step}
          stepIndex={run.stepIndex}
          total={total}
          status={run.status}
          paused={run.paused}
          onNext={() => advance('next')}
          onBack={back}
          onSkipStep={() => advance('skipped')}
          onDoItForMe={doItForMe}
          onPause={pause}
          onResume={resume}
          onExit={stop}
          onRetry={retry}
        />
      )}
      {children}
    </CoachContext.Provider>
  );
}

CoachProvider.propTypes = {
  children: PropTypes.node,
  enabled: PropTypes.bool,
  onGuideFinished: PropTypes.func,
  canRun: PropTypes.func,
};

export default CoachProvider;
